// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ErrorState
 * @package @geenius/analytics/solidjs
 * @description Internal recovery surface shared by SolidJS analytics data
 * components when a fetch or render boundary fails.
 */

import { Button } from "@geenius/ui/solidjs";
import { type Component, Show } from "solid-js";
import { cn } from "../lib/cn";

/** Error value accepted by SolidJS data-rendering components. */
export type AnalyticsErrorStateValue = Error | string | null | undefined;

/** Returns a user-visible message for a component error state. */
export function getAnalyticsErrorMessage(
  error: AnalyticsErrorStateValue,
): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === "string" && error.trim()) return error;
  return "Analytics data could not be loaded.";
}

interface ErrorStateProps {
  error: AnalyticsErrorStateValue;
  title?: string;
  onRetry?: () => void;
  class?: string;
}

/** Renders a compact, accessible error state with optional retry recovery. */
export const ErrorState: Component<ErrorStateProps> = (props) => (
  <div
    class={cn(
      "grid gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive",
      props.class,
    )}
    data-analytics-error-state
    role="alert"
    aria-live="assertive"
  >
    <strong class="font-semibold">
      {props.title ?? "Unable to load analytics"}
    </strong>
    <p>{getAnalyticsErrorMessage(props.error)}</p>
    <Show when={props.onRetry}>
      {(onRetry) => (
        <Button
          type="button"
          variant="secondary"
          class="w-fit"
          onClick={onRetry()}
        >
          Retry
        </Button>
      )}
    </Show>
  </div>
);
